const sheet = new CSSStyleSheet();
sheet.replaceSync(`
  :host {
    overflow: hidden;
  }
  :host > ::slotted([data-track]) {
    width: 100%;
    overflow: hidden;
  }
`);

class LabSlider extends HTMLElement {
  constructor() {
    super();

    this.attachShadow({mode: 'open'});
    this.shadowRoot.adoptedStyleSheets = [sheet];
    this.shadowRoot.innerHTML = `<slot></slot>`;

    this.isDraggable = this.hasAttribute('data-draggable');

    this.startDraggingBound = this.startDragging.bind(this);
    this.onDraggingBound = this.onDragging.bind(this);
    this.stopDraggingBound = this.stopDragging.bind(this);

    this.handlePreviousClickBound = this.handlePreviousClick.bind(this);
    this.handleNextClickBound = this.handleNextClick.bind(this);
    this.handleDotClickBound = this.handleDotClick.bind(this);
    this.sliderId = this.id || null;
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePreviousClickBound);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClickBound);
    }

    if (this.rotationInterval) {
      clearInterval(this.rotationInterval);
    }

    if (this.pagination.length > 0) {
      this.pagination.forEach((dot) => dot.removeEventListener('click', this.handleDotClickBound));
    }

    if (this.isDraggable) {
      this.slidesList.removeEventListener('mousedown', this.startDraggingBound);
      this.slidesList.removeEventListener('mousemove', this.onDraggingBound);
      this.slidesList.removeEventListener('mouseup', this.stopDraggingBound);
      this.slidesList.removeEventListener('mouseleave', this.stopDraggingBound);

      if (LabSlider.isTouchDevice()) {
        this.slidesList.removeEventListener('touchstart', this.startDraggingBound);
        this.slidesList.removeEventListener('touchmove', this.onDraggingBound, {passive: false});
        this.slidesList.removeEventListener('touchend', this.stopDraggingBound);
      }
    }
  }

  static get observedAttributes() {
    return ['active-slide'];
  }

  attributeChangedCallback(name, _, newValue) {
    if (name !== 'active-slide') return;

    this.goToSlide(newValue);
  }

  static isTouchDevice() {
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(max-width: 1023px)').matches
    );
  }

  render() {
    this.slidesList = this.querySelector('ul');
    this.sliderCount = this.querySelector('[data-slider-count]');

    if (!this.slidesList) {
      console.error("Error initializing LabSlider component: Missing 'ul' element.");

      return;
    }

    this.initChildElements();
    if (!this.slides.length) {
      console.error(
        "Error initializing LabSlider component: No slides 'li' found within the 'ul' element."
      );

      return;
    }

    this.prevButton = this.querySelector('button[data-action="previous"]');
    this.nextButton = this.querySelector('button[data-action="next"]');
    this.pagination = this.querySelectorAll('button[data-action="pagination"]');

    this.currentSlideIndex = Number(this.getAttribute('active-slide')) || 0;
    this.isSetToAutoRotate = this.getAttribute('auto-rotate') || 'false';
    this.sliderDurationInSecond = Number(this.getAttribute('slider-duration')) || 5;

    this.updateActiveSlideIndex();
    this.updateSlidesInView();
    this.updatePagination();

    if (this.isSetToAutoRotate === 'true') {
      this.mediaQuery = window.matchMedia('(max-width: 767px)');

      if (this.mediaQuery.matches) {
        this.startAutoRotating();
        return;
      }

      if (this.slidesInView === 1) {
        this.startAutoRotating();
      }
    }

    if (this.prevButton && this.nextButton) {
      this.initActionButtons();
    }

    if (this.pagination.length > 0) {
      this.initPagination();
    }

    if (this.isDraggable) {
      this.enableDragging();
    }
  }

  updateSlidesInView() {
    requestAnimationFrame(() => {
      const containerWidth = this.slidesList.clientWidth;
      const slideWidth = this.slides[0].offsetWidth;
      this.slidesInView = Math.floor(containerWidth / slideWidth);
    });
  }

  startAutoRotating() {
    const sliderDurationInMs = this.sliderDurationInSecond * 1000;

    this.rotationInterval = setInterval(this.handleNextClickBound, sliderDurationInMs);
  }

  goToSlide(slideIndex) {
    if (!this?.slides?.[slideIndex]) return;

    const scrollPosition = this.slides[slideIndex].offsetLeft - this.slidesList.offsetLeft;

    this.slidesList.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });

    if (this.sliderCount) {
      this.sliderCount.textContent = `${parseInt(slideIndex, 10) + 1} / ${this.slideCount}`;
    }
  }

  updateActiveSlideIndex() {
    this.setAttribute('active-slide', this.currentSlideIndex);

    this.slides.forEach((slide) => slide.removeAttribute('data-active-slide'));
    this.slides[this.currentSlideIndex].setAttribute('data-active-slide', '');

    if (this.isSetToAutoRotate === 'false') {
      if (this.prevButton) {
        this.prevButton.disabled = this.currentSlideIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.currentSlideIndex === this.slideCount - this.slidesInView;
      }
    }

    document.dispatchEvent(
      new CustomEvent(`lab-slider:slider-updated-${this.sliderId}`, {
        detail: {index: this.currentSlideIndex},
      })
    );
  }

  initActionButtons() {
    this.prevButton.addEventListener('click', this.handlePreviousClickBound);
    this.nextButton.addEventListener('click', this.handleNextClickBound);
  }

  handlePreviousClick() {
    if (this.currentSlideIndex - 1 < 0) {
      this.currentSlideIndex = this.slideCount - this.slidesInView;
    } else {
      this.currentSlideIndex -= 1;
    }

    this.updateActiveSlideIndex();
  }

  handleNextClick() {
    if (this.currentSlideIndex + 1 > this.slideCount - this.slidesInView) {
      this.currentSlideIndex = 0;
    } else {
      this.currentSlideIndex += 1;
    }

    this.updateActiveSlideIndex();
  }

  initPagination() {
    this.pagination.forEach((dot) => dot.addEventListener('click', this.handleDotClickBound));
  }

  initChildElements() {
    this.slides = this.slidesList.querySelectorAll(':scope > li');
    this.slideCount = this.slides.length;
  }

  updatePagination() {
    if (!this.pagination.length) return;

    for (let i = 0; i < this.slideCount; i += 1) {
      if (i === this.currentSlideIndex) {
        this.pagination[i].querySelector('svg').classList.add('active--dot');
      } else {
        this.pagination[i].querySelector('svg').classList.remove('active--dot');
      }
    }
  }

  handleDotClick(event) {
    this.currentSlideIndex = Number(event.currentTarget.getAttribute('data-pagination-index'));
    this.updateActiveSlideIndex();
    this.updatePagination();
  }

  enableDragging() {
    this.isDragging = false;
    this.dragTimer = null;
    this.dragStartXPosition = 0;
    this.dragLeftStartPosition = 0;

    this.slidesList.addEventListener('mousedown', this.startDraggingBound);
    this.slidesList.addEventListener('mousemove', this.onDraggingBound);
    this.slidesList.addEventListener('mouseup', this.stopDraggingBound);
    this.slidesList.addEventListener('mouseleave', this.stopDraggingBound);

    // Enable touch dragging only on mobile devices (<768px)
    if (LabSlider.isTouchDevice()) {
      this.slidesList.addEventListener('touchstart', this.startDraggingBound);
      this.slidesList.addEventListener('touchmove', this.onDraggingBound, {passive: false});
      this.slidesList.addEventListener('touchend', this.stopDraggingBound);
    }
  }

  startDragging(event) {
    this.isDragging = true;
    this.slidesList.classList.add('dragging');

    this.dragInitialXPosition = event?.touches?.[0]?.pageX || event.pageX;
    this.dragLeftStartPosition = this.slidesList.scrollLeft;

    // Detects a drag if the mouse is held for over 100ms, preventing links from redirecting when dragging instead of clicking.
    this.dragTimer = setTimeout(() => {
      this.slidesList.classList.add('drag-no-interaction');
    }, 100);
  }

  onDragging(event) {
    if (!this.isDragging) return;
    event.preventDefault();

    const x = event?.touches?.[0]?.pageX || event.pageX;
    this.dist = x - this.dragInitialXPosition;

    this.slidesList.scrollLeft = this.dragLeftStartPosition - this.dist;
  }

  stopDragging() {
    clearTimeout(this.dragTimer);

    if (!this.isDragging) return;
    this.isDragging = false;

    this.slidesList.classList.remove('dragging', 'drag-no-interaction');

    const slideWidth = this.slides[0].offsetWidth;
    const dragThreshold = slideWidth * 0.11; // Minimum drag distance to trigger slide change
    const roundingOffset = 0.5;
    const scrollPosition = this.slidesList.scrollLeft / slideWidth;
    let targetIndex;

    if (Math.abs(this.dist) < dragThreshold) {
      targetIndex = this.currentSlideIndex;
    } else {
      // We use the distance to either round up/down the scrolledPosition
      if (this.dist < 0) {
        targetIndex = Math.ceil(scrollPosition + roundingOffset);
      } else {
        targetIndex = Math.floor(scrollPosition - roundingOffset);
      }

      // Limit the change to only one slide from the current index.
      if (targetIndex > this.currentSlideIndex) {
        targetIndex = this.currentSlideIndex + 1;
      } else if (targetIndex < this.currentSlideIndex) {
        targetIndex = this.currentSlideIndex - 1;
      }
    }

    // Ensure index stays within the valid range (0 - last slide index).
    this.currentSlideIndex = Math.max(0, Math.min(targetIndex, this.slides.length - 1));

    this.updateActiveSlideIndex();
    this.updatePagination();

    document.dispatchEvent(
      new CustomEvent('dragging:stopped', {detail: {index: this.currentSlideIndex}})
    );
  }
}

customElements.define('lab-slider', LabSlider);
